import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Chart, registerables } from "chart.js";
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { Link } from "react-router-dom";
import "./style.css";
import "./FrequencyResponseFunction.css";
import DampingRatioCell from "./DampingRatioCell";
import FrfComparisonStatus from "./FrfComparisonStatus";
import {
  hasComparableDifference,
  isAmplitudeDifferenceAbnormal,
  isFrequencyDifferenceAbnormal
} from './frfBaseline';
import zoomPlugin from 'chartjs-plugin-zoom';
Chart.register(...registerables, zoomPlugin);

const SFRFZ = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fixeddata = location.state?.fixeddata || {};
  const tempdata = location.state?.tempdata || {};

  const { axis } = useParams();
  const [activeTab, setActiveTab] = useState(axis || "Z");
  const { id: machineId } = useParams();
  const parsedMachineId = parseInt(machineId);

  const lineChartRef = useRef(null);

  const [machineList, setMachineList] = useState([]);
  const [lineLabels, setlineLabels] = useState([]);
  const [coverData, setcoverData] = useState([]);
  const [freqDifferences, setFreqDifferences] = useState([]);
  const [ampDifferences, setAmpDifferences] = useState([]);

  const onMachineManagement = (e) => {
    e.preventDefault();
    navigate('/machine-management');
  };

  useEffect(() => {
    setActiveTab(axis || "Z");
  }, [axis]);

  const setDatas = (Data) => {
    const newLineLabels = Data.updataZ.chartData.freq.slice(9);
    const colors = getRandomColor();
    const newCoverData = [{
      label: Data.updataZ.chartData.ch_name,
      data: Data.updataZ.chartData.magn.slice(9),
      borderColor: colors.borderColor,
      backgroundColor: colors.backgroundColor
    }];
    return { lineLabels: newLineLabels, coverData: newCoverData };
  };

  const selectedMachine = machineList.find(machine => machine.id === parsedMachineId);
  const model = selectedMachine ? selectedMachine.model : '未知型號';
  const [selectedMachines, setSelectedMachines] = useState([]);

  const handleConfirmSelection = () => {
    const newChartData = selectedMachines.map((machineId) => {
      const machine = machineList.find((m) => m._id === machineId);
      if (!machine) return null;
      const r = Math.floor(Math.random() * 255);
      const g = Math.floor(Math.random() * 255);
      const b = Math.floor(Math.random() * 255);
      return {
        label: `${machine.model}`,
        borderColor: `rgb(${r}, ${g}, ${b})`,
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
        data: Array.isArray(machine.data.magn) ? machine.data.magn.slice(9) : [machine.data.magn.slice(9)],
        fill: true,
        tension: 0.4,
        labels: machine.data.freq
      };
    }).filter(item => item !== null);
    setlineLabels(newChartData[0]?.labels.slice(9) || [])
  };

  const createLineChart = (chartRef, datasets) => {
    const chartInstance = new Chart(lineChartRef.current, {
      type: "line",
      data: {
        labels: chartRef,
        datasets: datasets
      },
      options: {
        scales: {
          y: {
            ticks: { beginAtZero: true },
            title: { display: true, text: 'Amplitude (μm/N)', font: { size: 14 }, padding: 15 }
          },
          x: {
            type: 'linear',
            min: 15, max: 200,
            title: { display: true, text: 'Frequency (Hz)', font: { size: 14 }, padding: 15 },
            ticks: {
              beginAtZero: true,
              stepSize: 5,
              callback: function (value) {
                return Math.round(value);
              }
            }
          }
        },
        responsive: true,
        maintainAspectRatio: false,
        fill: true,
        elements: {
          point: { radius: 0, hitRadius: 10 },
          line: { tension: 0.4 }
        },
        plugins: {
          legend: {
            position: 'bottom',
            display: true,
            labels: {
              usePointStyle: true,
              pointStyle: 'rect',
              font: { size: 13 } // 折線圖圖例尺寸
            }
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || '';
                const xValue = context.label;
                const yValue = context.raw.toFixed(4);
                return `${label}   ( ${xValue} , ${yValue} )`;
              },
              title: function () {
                return '';
              }
            }
          },
          datalabels: { display: false },
          zoom: {
            pan: {
              enabled: false,
            },
            zoom: {
              wheel: { enabled: false },
              pinch: { enabled: false },
              mode: 'x',
              drag: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                threshold: 2,
                delay: 50
              }
            },
            onZoom: ({ chart }) => {
              let xScale = chart.scales.x;
              let min = Math.max(xScale.min, 15);
              let max = Math.min(xScale.max, 200);

              chart.options.scales.x.min = min;
              chart.options.scales.x.max = max;
              chart.options.scales.x.ticks.stepSize = 5;
              chart.update();
            }
          }
        }
      }
    });

    lineChartRef.current.chartInstance = chartInstance;
    return chartInstance;
  };

  const resetZoom = () => {
    if (lineChartRef.current && lineChartRef.current.chartInstance) {
      lineChartRef.current.chartInstance.resetZoom();
    }
  };

  function getRandomColor() {
    const r = Math.floor(Math.random() * 255);
    const g = Math.floor(Math.random() * 255);
    const b = Math.floor(Math.random() * 255);
    return {
      borderColor: `rgb(${r}, ${g}, ${b})`,
      backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
    };
  }

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        數值不在標準範圍內，為危險值！
      </Popover.Body>
    </Popover>
  );

  const DiffCount = (data1, data2) => {
    const newdata = data1.updataZ;
    const basedata = data2.updataZ;

    const freqDiffs = newdata.Amp_peak_freq_data.map(newFreq => {
      const match = basedata.Amp_peak_freq_data.find(baseFreq => {
        return Math.abs(newFreq - baseFreq) <= 1;
      });
      if (match !== undefined) {
        const diff = Math.abs(newFreq - match) / match;
        const diffpercent = (diff * 100).toFixed(1);
        return diffpercent;
      } else {
        return null;
      }
    });

    const ampDiffs = newdata.Amp_peak_data.map((newAmp, newIndex) => {
      const newFreq = newdata.Amp_peak_freq_data[newIndex];
      const matchIndex = basedata.Amp_peak_freq_data.findIndex(baseFreq => {
        return Math.abs(newFreq - baseFreq) <= 1;
      });
      if (matchIndex !== -1) {
        const baseAmp = basedata.Amp_peak_data[matchIndex];
        const diff = Math.abs((newAmp - baseAmp) / baseAmp);
        const diffpercent = (diff * 100).toFixed(1);
        return diffpercent;
      } else {
        return null;
      }
    });

    setFreqDifferences(freqDiffs);
    setAmpDifferences(ampDiffs);
  }

  useEffect(() => {
    const { lineLabels: newLineLabels, coverData: newCoverData } = setDatas(fixeddata);

    const updatedCoverData = newCoverData.map(dataset => ({
      ...dataset,
      label: `${dataset.label} (基準值)`
    }));

    if (lineChartRef.current) {
      if (!lineChartRef.current.chart) {
        lineChartRef.current.chart = createLineChart(newLineLabels, updatedCoverData);
      } else {
        lineChartRef.current.chart.data.labels = newLineLabels;
        lineChartRef.current.chart.data.datasets[0] = newCoverData[0];
        lineChartRef.current.chart.update();
      }
    }

    return () => {
      if (lineChartRef.current?.chart) {
        lineChartRef.current.chart.destroy();
      }
    };
  }, [fixeddata]);

  useEffect(() => {
    const { lineLabels: newLineLabels, coverData: newCoverData } = setDatas(tempdata);

    if (lineChartRef.current) {
      if (lineChartRef.current.chart) {
        lineChartRef.current.chart.data.labels = newLineLabels;

        if (lineChartRef.current.chart.data.datasets.length > 1) {
          lineChartRef.current.chart.data.datasets[1] = newCoverData[0];
        } else {
          lineChartRef.current.chart.data.datasets.push(newCoverData[0]);
        }

        lineChartRef.current.chart.update();
      }
    }
  }, [tempdata]);

  useEffect(() => {
    DiffCount(tempdata, fixeddata);
  }, [tempdata, fixeddata]);

  useEffect(() => {
    if (selectedMachines.length > 0) {
      handleConfirmSelection();
    }
  }, [activeTab]);

  return (
    <div className="d-flex" style={{ width: '81.7%', margin: '0 auto' }}>
      <div className="container-fluid">
        <div style={{ height: '35px', width: '100%' }}></div>

        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#" onClick={onMachineManagement} style={{ color: '#B0B7C3', fontWeight: 'bold', fontSize: '15px' }}>機台資料庫</a>
            </li>
            <li
              className="breadcrumb-item active"
              aria-current="page"
              style={{ color: '#5B82B1', fontWeight: 'bold', fontSize: '15px' }}>頻率響應函數 {tempdata.machineModel}
            </li>
          </ol>
        </nav>

        <div style={{ height: '35px', width: '100%' }}></div>

        <nav>
          <div className="nav nav-tabs" role="tablist">
            {["X", "Y", "Z"].map((axis) => (
              <Link
                key={axis}
                to={`/solo-frequency-response-function-${axis.toLowerCase()}/${machineId}`}
                className={`nav-link ${activeTab === axis ? "active" : ""}`}
                state={{ fixeddata, tempdata }}
              >
                {axis}軸向
              </Link>
            ))}
          </div>
        </nav>

        <div className="tab-content" id="nav-tabContent">
          <div className="tab-pane show active p-0 my-4">
            <div className="frf-action-row">
              <button
                className="reset-btn bi bi-arrow-clockwise"
                aria-label="重設圖表縮放"
                onClick={resetZoom}
              >
              </button>
            </div>

            <FrfComparisonStatus
              frequencyDifferences={freqDifferences}
              amplitudeDifferences={ampDifferences}
            />

            <div style={{ width: '100%', height: '500px' }}>
              <canvas ref={lineChartRef}></canvas>
            </div>

            <div className="table-margin">
              <div className="table-responsive">
                <table className="table table-striped table-bordered text-center frf-peak-table">
                  <colgroup>
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                    <col style={{ width: '17.6%' }} />
                  </colgroup>
                  <thead className="custom-thead ">
                    <tr>
                      <th>資料ID</th>
                      <th>頻率</th>
                      <th>振幅</th>
                      <th>阻尼比</th>
                      <th>線差(f)</th>
                      <th>幅差(a)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(
                      (freqDifferences?.length > 0 ? freqDifferences : tempdata.updataZ.Amp_peak_freq_data) || []
                    )
                      .map((_, index) => index)
                      .filter(index =>
                        hasComparableDifference(freqDifferences?.[index]) &&
                        hasComparableDifference(ampDifferences?.[index])
                      )
                      .map((index, displayIndex) => {
                        const freq = tempdata.updataZ.Amp_peak_freq_data?.[index] || 'NaN';
                        const amp = freq === 'NaN' ? 'NaN' : (tempdata.updataZ.Amp_peak_data?.[index] || 'NaN');
                        const freqDiff = freqDifferences?.[index];
                        const ampDiff = ampDifferences?.[index];
                        const hasFreqDiff = hasComparableDifference(freqDiff);
                        const hasAmpDiff = hasComparableDifference(ampDiff);
                        const hasComparison = hasFreqDiff && hasAmpDiff;
                        return (
                          <tr key={index}>
                            <td>{displayIndex + 1}</td>
                            <td style={{ color: freq === 'NaN' ? '#CCCCCC' : '' }}>{freq}</td>
                            <td style={{ color: amp === 'NaN' ? '#CCCCCC' : '' }}>{amp}</td>
                            <DampingRatioCell value={tempdata.updataZ.Damping_ratio_data?.[index]} />

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={hasComparison && isFrequencyDifferenceAbnormal(freqDiff) ? popover : <></>}
                            >
                              <td
                                style={{ color: hasComparison && isFrequencyDifferenceAbnormal(freqDiff) ? '#EC4643' : (!hasComparison ? '#999' : ''), }}
                              >
                                {hasComparison ? `${freqDiff}%` : '-'}
                              </td>
                            </OverlayTrigger>

                            <OverlayTrigger
                              trigger={['hover', 'focus']}
                              placement="top"
                              overlay={
                                (() => {
                                  if (hasComparison && isAmplitudeDifferenceAbnormal(ampDiff)) {
                                    return popover;
                                  }
                                  return <></>;
                                })()
                              }
                            >
                              <td
                                style={{
                                  color: (() => {
                                    if (hasComparison && isAmplitudeDifferenceAbnormal(ampDiff)) {
                                      return '#EC4643';
                                    }
                                    return !hasComparison ? '#999' : '';
                                  })()
                                }}
                              >
                                {hasComparison ? `${ampDiff}%` : '-'}
                              </td>
                            </OverlayTrigger>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default SFRFZ;
